import { useEffect, useMemo, useState } from "react";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import appraisalOrderForm from "../../data/appraisalOrder_form.json";
import {
    buildInitialValues,
    collectFields,
    contactField,
    normalizeFromEncompass,
    normalizeForEncompassWrite,
    syncHostLoanWorkspace,
    datetimeLocalToMdyHm,
    LabeledField,
    Control
} from "./appraisalOrderFormHelpers";

import { fieldReader, fieldWriter } from "../../services/loanApi";
import { getBrokerLoAeAssignment } from "../../services/userApi";
import {
    applyCustomerDivisionOptions,
    applyServiceProviderOptions,
    getDefaultCustomerPk,
    getTeamCustomerPk,
    isCustomerDivisionByAeTeam
} from "../../utils/loanHelper";
import { getAccessToken } from "../../services/authApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";

// Customer Division (CX.CMS.CUSTOMERPK) and Service Provider (CX.CMS.SERVICEPROVIDERID)
// options for this org + environment
const appraisalForm = applyServiceProviderOptions(applyCustomerDivisionOptions(appraisalOrderForm));

export default function AppraisalOrderPage() {
    const form = appraisalForm;

    const [token, setToken] = useState(null);
    const [loanid, setLoanid] = useState("");
    const [loanNumber, setLoanNumber] = useState('');

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");

    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState("success");
    const [toastMessage, setToastMessage] = useState("");
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");

    const [skipFetch, setSkipFetch] = useState(false);
    const [values, setValues] = useState(() => buildInitialValues(form));
    const [shouldSaveCustomerDivision, setShouldSaveCustomerDivision] = useState(false);
    const [userName, setUserName] = useState("");

    const [isPageReadOnly, setIsPageReadOnly] = useState(false);
    const [readOnlyMessage, setReadOnlyMessage] = useState("");
    const [restrictedFieldIds, setRestrictedFieldIds] = useState([]);
    const [canEditRestrictedFields, setCanEditRestrictedFields] = useState(false);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    const raw = await loanObject.all();
                    const loan = raw?.loan ?? raw;
                    setLoanid(loan.id);
                    setLoanNumber(loan.loanNumber);
                    //const tpo = loan?.tpo || {};
                    //const submittedDate = tpo.initialSubmitDate || '';
                    //setInitialSubmitDate(submittedDate);

                    await getToken(false);
                });
            } catch (error) {
                const errorCode = logError("APPRAISAL_ORDER_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "AppraisalOrder",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                console.error("Auth block Error:", error);
                setLoading(false);
            }
        } else {
            console.warn("elli.script.guest is not available.");
            setLoading(false);
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const user = await tpoAuthObject.getUser();
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
            setSkipFetch(skipFieldLoad);
            setUserName(user.firstName + " " + user.lastName);
        } catch (err) {
            const errorCode = logError("APPRAISAL_ORDER_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "AppraisalOrder",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };



    const sections = useMemo(
        () => (Array.isArray(form?.sections) ? form.sections : []),
        [form]
    );

    const allFields = useMemo(() => collectFields(form), [form]);

    const uiFieldsWithEncompass = useMemo(
        () => allFields.filter((f) => !!f?.encompassId),
        [allFields]
    );

    const encompassIds = useMemo(() => {
        const ids = [];
        uiFieldsWithEncompass.forEach((f) => {
            if (f.encompassId) ids.push(f.encompassId);
            if (f.encompassIdLast) ids.push(f.encompassIdLast);
        });
        // NEWCUSTOMFIELD is a placeholder for fields not yet provisioned in Encompass
        ids.push("TPO.X63"); // LO email for a missing Customer Division
        return [...new Set(ids.filter((id) => id && id !== "NEWCUSTOMFIELD"))];
    }, [uiFieldsWithEncompass]);

    const showToastMsg = (msg, type = "success") => {
        setToastMessage(msg);
        setToastType(type);
        setToastVisible(true);
        setToastAnimation("animate-fade-in-right");

        setTimeout(() => {
            setToastAnimation("animate-fade-out-right");
            setTimeout(() => setToastVisible(false), 500);
        }, 8000);
    };

    // BR-009: fields whose change refreshes the per-appraisal audit stamp
    const AUDIT_TRIGGERS = {
        app1: ["app1_providerType", "app1_transferOrderDate", "app1_transferDeliveryDate"],
        app2: ["app2_providerType", "app2_transferOrderDate", "app2_transferDeliveryDate"]
    };

    const nowDatetimeLocal = () => {
        const d = new Date();
        const p = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    };

    const onChange = (id, v) => {
        setValues((prev) => {
            const next = { ...prev, [id]: v };
            const who = (userName || "").trim();

            // BR-008 / BR-009: stamp the modified appraisal record independently (AC-04, AC-17)
            if (who) {
                if (AUDIT_TRIGGERS.app1.includes(id)) {
                    next["app1_completedBy"] = who;
                    next["app1_completedDate"] = nowDatetimeLocal();
                } else if (AUDIT_TRIGGERS.app2.includes(id)) {
                    next["app2_completedBy"] = who;
                    next["app2_completedDate"] = nowDatetimeLocal();
                }
            }

            return next;
        });
    };

    const isFieldForceReadOnly = (field) => {
        const encompassId = String(field?.encompassId || "").trim().toLowerCase();
        if (!encompassId) return false;

        const restricted = restrictedFieldIds.map((x) => String(x || "").trim().toLowerCase());
        if (!restricted.includes(encompassId)) return false;

        return !canEditRestrictedFields;
    };

    // ---- init guest script (user + loan + token) ----
    /*useEffect(() => {
        const initGuestScriptAsync = async () => {
            if (window.elli && window.elli.script && window.elli.script.guest) {
                try {
                    window.elli.script.guest.create();

                    window.elli.script.getObject("loan").then(async function (loanObject) {
                        loanObject.all().then(async function (loan) {
                            setLoanid(loan.id);
                            setLoanNumber(loan.loanNumber);
                            //const tpo = loan?.tpo || {};
                            //const submittedDate = tpo.initialSubmitDate || '';
                            //setInitialSubmitDate(submittedDate);

                            await getToken(false);
                        });
                    });

                    await getToken();
                } catch (error) {
                    const errorCode = logError("APPRAISAL_ORDER_LOAN_FETCH_ERROR");
                    logAppInsightError("Loan Fetch Error", {
                        component: "AppraisalOrderPage",
                        problemId: errorCode,
                        errorCode,
                        rawError: error,
                        errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                    });
                    setLoading(false);
                }
            } else {
                console.warn("elli.script.guest is not available.");
            }
        };

        initGuestScriptAsync();
    }, []);*/

    /*const getToken = async () => {
        setLoading(true);
        setLoadingText("Loading Appraisal Order...");
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const tkn = await getAccessToken(authCode);
            setToken(tkn);

            const user = await tpoAuthObject.getUser();
            setUserName(user.firstName + " " + user.lastName);
        } catch (err) {
            const errorCode = logError("APPRAISAL_ORDER_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "AppraisalOrderPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };*/

    // ---- read encompass fields ----
    const fetchFields = async () => {
        if (!token || !loanid) return;
        if (!encompassIds.length) return;

        setLoading(true);
        setLoadingText("Loading Appraisal Order...");
        try {
            const data = await fieldReader(token, encompassIds, loanid);
            const savedCustomerPk = String(data?.["CX.CMS.CUSTOMERPK"] ?? "").trim();
            let mappedCustomerPk = "";

            if (!savedCustomerPk && !isCustomerDivisionByAeTeam()) {
                // SkyTPO: no AE-team lookup - default to the first Customer Division option
                mappedCustomerPk = getDefaultCustomerPk();
            } else if (!savedCustomerPk) {
                const loEmail = String(data?.["TPO.X63"] ?? "").trim();
                if (loEmail) {
                    try {
                        const authObject = await window.elli.script.getObject("auth");
                        const authCode = await authObject.createAuthCode();
                        const assignment = await getBrokerLoAeAssignment(authCode, loEmail);
                        mappedCustomerPk = getTeamCustomerPk(assignment?.team ?? assignment?.Team);
                    } catch (assignmentError) {
                        console.error("Broker LO AE assignment lookup failed:", assignmentError);
                    }
                }
            }

            setShouldSaveCustomerDivision(!savedCustomerPk && !!mappedCustomerPk);
            setValues((prev) => {
                const next = { ...prev };
                for (const f of uiFieldsWithEncompass) {
                    if (!f.encompassId || f.encompassId === "NEWCUSTOMFIELD") continue;

                    // dual-id name (borrower / co-borrower) => join first + last
                    if (f.encompassIdLast) {
                        const first = String(data?.[f.encompassId] ?? "").trim();
                        const last = String(data?.[f.encompassIdLast] ?? "").trim();
                        next[f.id] = [first, last].filter(Boolean).join(" ");
                        continue;
                    }

                    next[f.id] = normalizeFromEncompass(f, data?.[f.encompassId]);
                }
                if (mappedCustomerPk) next.customerDivision = mappedCustomerPk;
                return next;
            });
        } catch (err) {
            const errorCode = logError("APPRAISAL_ORDER_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "AppraisalOrderPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            showToastMsg("Failed to load Appraisal Order fields.", "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    useEffect(() => {
        if (!token) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }
        fetchFields();
    }, [token, loanid]);

    const buildChangesPayload = (vals) => {
        const byEncompassId = new Map();
        const updatable = uiFieldsWithEncompass.filter((f) => f.isUpdate !== false && f.readonly !== true);

        for (const f of updatable) {
            const eid = f.encompassId;
            // skip empty ids and not-yet-provisioned placeholder fields
            if (!eid || eid === "NEWCUSTOMFIELD") continue;

            // dual-id name (borrower / co-borrower) => split full name into first + last
            if (f.encompassIdLast) {
                const parts = String(vals?.[f.id] ?? "").trim().split(/\s+/);
                const first = parts.shift() || "";
                const last = parts.join(" ");
                byEncompassId.set(eid, { id: eid, value: first });
                byEncompassId.set(f.encompassIdLast, { id: f.encompassIdLast, value: last });
                continue;
            }

            byEncompassId.set(eid, { id: eid, value: normalizeForEncompassWrite(f, vals?.[f.id] ?? "") });
        }

        // Persist the LO assignment when the saved Customer Division was empty.
        if (shouldSaveCustomerDivision && String(vals?.customerDivision ?? "").trim()) {
            byEncompassId.set("CX.CMS.CUSTOMERPK", { id: "CX.CMS.CUSTOMERPK", value: vals.customerDivision });
        }

        // derived: Provider Type "Transferred Appraisal" => CU transferred indicator = "Yes", else "No"
        const app1Transferred = vals?.app1_providerType === "Transferred Appraisal" ? "Yes" : "No";
        const app2Transferred = vals?.app2_providerType === "Transferred Appraisal" ? "Yes" : "No";
        byEncompassId.set("CX.APPR.CU.TRANSFERRED", { id: "CX.APPR.CU.TRANSFERRED", value: app1Transferred });
        byEncompassId.set("CX.APPR2.CU.TRANSFERRED", { id: "CX.APPR2.CU.TRANSFERRED", value: app2Transferred });

        // When the loan has one or more ADUs, set the ADU indicator to Yes.
        if (Number(vals?.aduCount) > 0) {
            byEncompassId.set("URLA.X309", { id: "URLA.X309", value: "true" });
        }

        // BR-008/BR-009: persist the per-appraisal audit stamp (Completed By / Date).
        // These are readonly in the UI (so skipped by the loop above) but are set in
        // state by onChange when a provider field changes, and must be written to
        // Encompass. Only write when a value exists so an untouched appraisal isn't cleared.
        for (const id of ["app1_completedBy", "app1_completedDate", "app2_completedBy", "app2_completedDate", "productType"]) {
            const f = uiFieldsWithEncompass.find((x) => x.id === id);
            const eid = f?.encompassId;
            if (!eid || eid === "NEWCUSTOMFIELD") continue;
            const raw = vals?.[id];
            if (raw == null || String(raw).trim() === "") continue;
            byEncompassId.set(eid, { id: eid, value: normalizeForEncompassWrite(f, raw) });
        }

        return [...byEncompassId.values()];
    };

    const getEncompassErrorDetails = (e) => {
        const errors = e?.response?.data?.errors;
        if (!Array.isArray(errors) || !errors.length) return null;
        return errors.map((x) => x?.details).filter(Boolean).join("\n");
    };

    // ---- business-rule validation (VAL-01..04, BR-007/014/033) ----
    const getValidationError = () => {

        // VAL-01 / VAL-02 / BR-007: transferred appraisal requires both transfer dates
        const checkTransfer = (label, typeId, orderId, deliveryId) => {
            if ((values[typeId] || "") !== "Transferred Appraisal") return null;
            if (!(values[orderId] || "").trim()) {
                return `${label}: Transfer In Appraisal Order Date is required when Provider Type is "Transferred Appraisal".`;
            }
            if (!(values[deliveryId] || "").trim()) {
                return `${label}: Transfer In Expected Appraisal Delivery Date is required when Provider Type is "Transferred Appraisal".`;
            }
            return null;
        };

        // VAL-04 / BR-014: Service Provider required
        if (!(values["serviceProvider"] || "").trim()) {
            return "Service Provider is required. Please select a service provider.";
        }
        // / BR-015: Appraisal Type required
        if (!(values["appraisalType"] || "").trim()) {
            return "Appraisal Type is required. Please select an appraisal type.";
        }

        // required loan-data fields
        const requiredFields = [
            ["productType", "Product Type"],
            ["loanPurpose", "Loan Purpose"],
            ["customerDivision", "Customer Division"],
            ["propertyType", "Property Type"],
            ["attachmentType", "Attachment Type"],
            ["propertyUsageType", "Property Usage Type"],
            ["livingUnits", "Living Units (Excluding ADU)"]
        ];
        for (const [id, label] of requiredFields) {
            if (!String(values[id] ?? "").trim()) {
                return `${label} is required.`;
            }
        }

        const app1Err = checkTransfer("Appraisal 1", "app1_providerType", "app1_transferOrderDate", "app1_transferDeliveryDate");
        if (app1Err) return app1Err;
        const app2Err = checkTransfer("Appraisal 2", "app2_providerType", "app2_transferOrderDate", "app2_transferDeliveryDate");
        if (app2Err) return app2Err;

        // VAL-03 / BR-033: at least one contact marked Yes
        const contactsSection = sections.find((s) => s.layout === "contacts");
        const anyYes = (contactsSection?.contacts || []).some(
            (c) => (values[c.canContact?.id] || "") === "Yes"
        );
        if (contactsSection && !anyYes) {
            return contactsSection.validationNote ||
                'At least one contact must be marked "Yes" for Can Be Contacted By Service Provider.';
        }

        return null;
    };

    // ---- save/write encompass fields ----
    const onSave = async (isRetry = false) => {
        if (!token || !loanid) {
            showToastMsg("Missing token or loan id.", "error");
            return;
        }

        const validationError = getValidationError();
        if (validationError) {
            showToastMsg(validationError, "error");
            return;
        }

        setLoading(true);
        setLoadingText("Saving Appraisal Order...");

        try {
            const updatedFields = buildChangesPayload(values);
            await fieldWriter(token, loanid, updatedFields);
            await syncHostLoanWorkspace();
            await fetchFields();
            showToastMsg("Appraisal Order saved successfully.", "success");
        } catch (e) {
            if (e?.response?.status === 401 && !isRetry) {
                await getToken();
                return onSave(true);
            }

            const errorCode = logError("APPRAISAL_ORDER_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "AppraisalOrderPage",
                problemId: errorCode,
                errorCode,
                rawError: e,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            const allDetails = getEncompassErrorDetails(e);
            const serverMsg = e?.response?.data?.details;
            showToastMsg(
                allDetails
                    ? `Save failed:\n${allDetails}`
                    : serverMsg
                        ? `Save failed: ${serverMsg}`
                        : "Save failed.",
                "error"
            );
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    // ---- section header bar ----
    const SectionHeader = ({ title, note }) => (
        <div className="flex items-center gap-2 border-b border-sky-200 pb-2 mb-4">
            <h2 className="text-[15px] font-bold text-[#1e3a8a] tracking-wide uppercase">
                {title}
            </h2>
            {note && <span className="text-xs text-sky-700 font-normal">{note}</span>}
        </div>
    );

    // ---- provider info (Appraisal 1 / Appraisal 2 rows) ----
    const renderProviderInfo = (section) => (
        <div className="bg-white border rounded-lg p-4">
            <SectionHeader title={section.title} />
            <div className="space-y-4">
                {(section.rows || []).map((row) => {
                    // BR-007: transfer dates become required when Provider Type = Transferred Appraisal
                    const isTransferred =
                        (values[`${row.appraisal}_providerType`] || "") === "Transferred Appraisal";
                    return (
                        <div key={row.label} className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-12 md:col-span-1 flex items-center justify-center md:justify-start pt-6">
                                <span className="text-[#1e3a8a] font-bold text-sm">{row.label}</span>
                            </div>
                            <div className="col-span-12 md:col-span-11">
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    {(row.fields || []).map((f) => {
                                        const isTransferDate =
                                            f.id.endsWith("_transferOrderDate") ||
                                            f.id.endsWith("_transferDeliveryDate");
                                        const field =
                                            isTransferDate && isTransferred
                                                ? { ...f, required: true }
                                                : f;
                                        return (
                                            <LabeledField
                                                key={f.id}
                                                field={field}
                                                value={values[f.id]}
                                                onChange={onChange}
                                                pageReadOnly={isPageReadOnly}
                                                fieldForceReadOnly={isFieldForceReadOnly(f)}
                                            />
                                        );
                                    })}
                                </div>
                                {row.note && (
                                    <div className="text-xs text-sky-700 mt-1">{row.note}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ---- generic labeled grid ----
    const renderGrid = (section) => {
        const cols = section.columns || 4;
        const colClass =
            {
                2: "md:grid-cols-2",
                3: "md:grid-cols-3",
                4: "md:grid-cols-4",
                5: "md:grid-cols-5",
                6: "md:grid-cols-6"
            }[cols] || "md:grid-cols-4";

        return (
            <div className="bg-white border rounded-lg p-4">
                <SectionHeader title={section.title} note={section.titleNote} />
                <div className={`grid grid-cols-1 ${colClass} gap-x-6 gap-y-4`}>
                    {(section.fields || []).map((f) => (
                        <div
                            key={f.id}
                            style={f.gridColumn ? { gridColumnStart: f.gridColumn } : undefined}
                        >
                            <LabeledField
                                field={f}
                                value={values[f.id]}
                                onChange={onChange}
                                pageReadOnly={isPageReadOnly}
                                fieldForceReadOnly={isFieldForceReadOnly(f)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ---- inline checkbox row ----
    const renderCheckboxes = (section) => (
        <div className="bg-white border rounded-lg p-4">
            <SectionHeader title={section.title} note={section.titleNote} />
            <div className="flex items-center gap-10">
                {(section.fields || []).map((f) => (
                    <Control
                        key={f.id}
                        field={f}
                        value={values[f.id]}
                        onChange={onChange}
                        pageReadOnly={isPageReadOnly}
                        fieldForceReadOnly={isFieldForceReadOnly(f)}
                    />
                ))}
            </div>
        </div>
    );

    // ---- property contacts table ----
    const renderContacts = (section) => {
        const findExtra = (id) => (section.extraFields || []).find((f) => f.id === id);
        const lockBox = findExtra("lockBoxCombo");
        const access = findExtra("accessInstructions");

        return (
            <div className="bg-white border rounded-lg p-4">
                <SectionHeader title={section.title} note={section.titleNote} />

                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="text-left text-gray-600 border-b">
                                <th className="py-2 pr-3 font-semibold">Contact Type</th>
                                <th className="py-2 pr-3 font-semibold">Name</th>
                                <th className="py-2 pr-3 font-semibold">Email</th>
                                <th className="py-2 pr-3 font-semibold">Phone</th>
                                <th className="py-2 pr-3 font-semibold">Can Be Contacted By Service Provider</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(section.contacts || []).map((c) => {
                                const nameF = contactField("name", c.name);
                                const emailF = contactField("email", c.email);
                                const phoneF = contactField("phone", c.phone);
                                const canF = contactField("canContact", c.canContact);
                                return (
                                    <tr key={c.type} className="border-b last:border-b-0">
                                        <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{c.type}</td>
                                        <td className="py-2 pr-3">
                                            <Control
                                                field={nameF}
                                                value={values[nameF.id]}
                                                onChange={onChange}
                                                pageReadOnly={isPageReadOnly}
                                                fieldForceReadOnly={isFieldForceReadOnly(nameF)}
                                            />
                                        </td>
                                        <td className="py-2 pr-3">
                                            <Control
                                                field={emailF}
                                                value={values[emailF.id]}
                                                onChange={onChange}
                                                pageReadOnly={isPageReadOnly}
                                                fieldForceReadOnly={isFieldForceReadOnly(emailF)}
                                            />
                                        </td>
                                        <td className="py-2 pr-3">
                                            <Control
                                                field={phoneF}
                                                value={values[phoneF.id]}
                                                onChange={onChange}
                                                pageReadOnly={isPageReadOnly}
                                                fieldForceReadOnly={isFieldForceReadOnly(phoneF)}
                                            />
                                        </td>
                                        <td className="py-2 pr-3">
                                            <Control
                                                field={canF}
                                                value={values[canF.id]}
                                                onChange={onChange}
                                                pageReadOnly={isPageReadOnly}
                                                fieldForceReadOnly={isFieldForceReadOnly(canF)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                    {lockBox && (
                        <div>
                            <LabeledField
                                field={lockBox}
                                value={values[lockBox.id]}
                                onChange={onChange}
                                pageReadOnly={isPageReadOnly}
                                fieldForceReadOnly={isFieldForceReadOnly(lockBox)}
                            />
                        </div>
                    )}
                    {access && (
                        <div className="md:col-span-2">
                            <LabeledField
                                field={access}
                                value={values[access.id]}
                                onChange={onChange}
                                pageReadOnly={isPageReadOnly}
                                fieldForceReadOnly={isFieldForceReadOnly(access)}
                            />
                        </div>
                    )}
                </div>

                {section.validationNote && (
                    <div className="text-xs text-sky-700 mt-3">{section.validationNote}</div>
                )}
            </div>
        );
    };

    // ---- footer (loan officer / broker / processor) ----
    const renderFooter = (section) => (
        <div className="bg-white border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {(section.fields || []).map((f) => (
                    <LabeledField
                        key={f.id}
                        field={f}
                        value={values[f.id]}
                        onChange={onChange}
                        pageReadOnly={isPageReadOnly}
                        fieldForceReadOnly={isFieldForceReadOnly(f)}
                    />
                ))}
            </div>
        </div>
    );

    const completedBy = values["app1_completedBy"] || userName;
    const completedDate = datetimeLocalToMdyHm(values["app1_completedDate"] || "");

    return (
        <div className="rr-page space-y-6 p-25px">
            <LoadingOverlay loading={loading} text={loadingText} />

            <div className="rounded-lg p-4 space-y-6 max-w-8xl mx-auto">
                {/* ===== page header ===== */}
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <h1 className="text-2xl font-bold text-[#1e3a8a]">
                        {form.title}
                    </h1>
                    <div className="flex items-center gap-6 text-sm text-gray-700">
                        {completedBy && (
                            <div>
                                <span className="font-semibold text-gray-600">Completed By: </span>
                                {completedBy}
                            </div>
                        )}
                        {completedDate && (
                            <div>
                                <span className="font-semibold text-gray-600">Date/Time: </span>
                                {completedDate}
                            </div>
                        )}
                        {/* Floating Save Button */}
                        {!isPageReadOnly && (
                            <div className="fixed top-1 right-4 z-50">
                                <button
                                    className="btn-base btn-next shadow-lg"
                                    onClick={() => onSave(false)}
                                    disabled={loading}
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {isPageReadOnly && (
                    <div className="border border-yellow-300 bg-yellow-50 text-yellow-800 rounded-lg px-4 py-3">
                        {readOnlyMessage || "This page is read-only for your persona."}
                    </div>
                )}



                {/* ===== sections ===== */}
                {sections.map((sec) => {
                    if (sec.layout === "provider-info")
                        return <div key={sec.key}>{renderProviderInfo(sec)}</div>;
                    if (sec.layout === "grid")
                        return <div key={sec.key}>{renderGrid(sec)}</div>;
                    if (sec.layout === "checkboxes")
                        return <div key={sec.key}>{renderCheckboxes(sec)}</div>;
                    if (sec.layout === "contacts")
                        return <div key={sec.key}>{renderContacts(sec)}</div>;
                    if (sec.layout === "footer")
                        return <div key={sec.key}>{renderFooter(sec)}</div>;
                    return null;
                })}

                {/* ===== bottom save ===== */}
                {!isPageReadOnly && (
                    <div className="flex items-center justify-end">
                        <button
                            className="btn-base btn-next"
                            onClick={() => onSave(false)}
                            disabled={loading}
                        >
                            Save
                        </button>
                    </div>
                )}

                <Toast
                    visible={toastVisible}
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setToastVisible(false)}
                    animationClass={toastAnimation}
                />
            </div>
        </div>
    );
}
